import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiShield, FiZap, FiHeart, FiGlobe, FiUsers } = FiIcons;

function AboutSection() {
  const features = [
    {
      icon: FiTarget,
      title: 'Στοχευμένες Λύσεις',
      description: 'Κάθε υπηρεσία προσαρμόζεται στις ειδικές ανάγκες της επιχείρησής σας για μέγιστη αποδοτικότητα.'
    },
    {
      icon: FiShield,
      title: 'Αξιοπιστία & Ασφάλεια',
      description: 'Πιστοποιημένες τεχνολογίες και πρωτόκολλα ασφαλείας για την προστασία των δεδομένων σας.'
    },
    {
      icon: FiZap,
      title: 'Γρήγορη Υλοποίηση',
      description: 'Άμεση εφαρμογή λύσεων με minimal downtime και συνεχή υποστήριξη καθ\' όλη τη διαδικασία.'
    },
    {
      icon: FiHeart,
      title: 'Προσωπική Προσέγγιση',
      description: 'Dedicated account manager για κάθε πελάτη με 24/7 τεχνική υποστήριξη και συμβουλευτική.'
    }
  ];

  const companies = [
    {
      icon: FiGlobe,
      title: 'Apollon Chania',
      description: 'Τεχνική υποστήριξη & υποδομές',
      link: 'https://apollon-chania.eu'
    },
    {
      icon: FiUsers,
      title: 'MyMarketHub',
      description: 'Digital marketing & προώθηση',
      link: 'https://mymarkethub.eu'
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ποιοι είμαστε & τι προσφέρουμε
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Το <strong>CodeVaro.eu</strong> είναι το επίσημο portal των εταιρειών μας που ενώνει 
            περισσότερες από <strong>90 εξειδικευμένες υπηρεσίες</strong> σε έναν ενιαίο προορισμό. 
            Από τεχνική υποστήριξη μέχρι AI λύσεις - όλα σε ένα μέρος.
          </p>
        </motion.div>

        {/* Company Cards */}
        <motion.div
          className="grid md:grid-cols-2 gap-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {companies.map((company, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={company.icon} className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{company.title}</h3>
                  <p className="text-gray-600">{company.description}</p>
                </div>
              </div>
              <a 
                href={company.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors"
              >
                {company.link.replace('https://', '')}
              </a>
            </div>
          ))}
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -5 }}
            >
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Έτοιμοι να ξεκινήσουμε;</h3>
            <p className="text-xl text-blue-100 mb-6">
              Κλείστε ένα δωρεάν ραντεβού και ανακαλύψτε πώς μπορούμε να βελτιώσουμε την επιχείρησή σας
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://tidycal.com/kostashania/short"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Δωρεάν Ραντεβού
              </motion.a>
              <motion.button
                className="border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-blue-600 transition-colors font-semibold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('projects')}
              >
                Δες Παραδείγματα
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default AboutSection;